import {
  largeDecimalFormat,
  smallCurrencyFormat,
} from 'common/formats/formats';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import {
  BOLD_DIVISION_ROW_HEADER_CONFIG,
  TABLE_HEADER_CONFIG
} from 'pages/Valuations/approaches/backsolveApproach/constants';
import SecuritySelectionViewer from 'pages/Valuations/approaches/backsolveApproach/SecurityTable/ components/SecuritySelectionViewer';
import SecurityGridSelect from 'pages/Valuations/approaches/backsolveApproach/SecurityTable/SecurityGridSelect';
import {
  PER_SHARE_ALIAS,
  PER_SHARE_TITLE,
  SECURITY_ALIAS,
  SECURITY_TITLE,
  SHARES_ALIAS,
  SHARES_TITLE,
  TARGET_VALUE_ALIAS,
  TOTAL_VALUE_ALIAS,
  TOTAL_VALUE_TITLE,
} from 'pages/Valuations/approaches/backsolveApproach/SecurityTable/util/constants';
import { SOURCE_TOTALS_ROW_TARGET } from 'pages/Valuations/approaches/backsolveApproach/SourceRowsTable/sheetAliases';
import { ParserProps } from './type';

export default async ({ rowConfig, tableData }: ParserProps) => {
  const { isDisabled, isTargetValueReadOnly, sourceRowsSheetName } = tableData;
  const rowOffset = 2;
  const customClasses = `${TABLE_HEADER_CONFIG.className} text-right`;

  const cells: Record<string, Partial<ICell>> = {
    A1: {
      ...TABLE_HEADER_CONFIG,
      value: SECURITY_TITLE,
      key: 'A1',
    },
    B1: {
      ...TABLE_HEADER_CONFIG,
      className: customClasses,
      value: SHARES_TITLE,
      key: 'B1',
    },
    C1: {
      ...TABLE_HEADER_CONFIG,
      className: customClasses,
      value: PER_SHARE_TITLE,
      key: 'C1',
    },
    D1: {
      ...TABLE_HEADER_CONFIG,
      className: customClasses,
      value: TOTAL_VALUE_TITLE,
      key: 'D1',
    },
  };
  // eslint-disable-next-line max-len
  const referencedSecurities = tableData.securitiesBasket?.basket;
  const totalsRowNumber = rowConfig.length;
  const getSumExprCells = (colLegend: string) =>
    referencedSecurities?.map((_: any, i: number) => colLegend + (i + rowOffset)).join(',');
  const TOTAL_LABEL = {
    ...BOLD_DIVISION_ROW_HEADER_CONFIG,
    className: `${BOLD_DIVISION_ROW_HEADER_CONFIG.className} division-top-only`,
    value: 'Target Value',
    key: `A${totalsRowNumber}`,
  };
  const SHARES_TOTAL_CELL = {
    readOnly: true,
    value: '',
    key: `B${totalsRowNumber}`,
    expr: `=SUM(${getSumExprCells('B')})`,
    format: largeDecimalFormat,
    gridType: 'number',
  };
  const targetValueFromBasket = tableData.securitiesBasket?.target_value ?? 0;
  const PER_SHARE_TOTAL_CELL = isTargetValueReadOnly
    ? {
      readOnly: true,
      value: Number(targetValueFromBasket) || 0,
      key: `C${totalsRowNumber}`,
      expr: `=D${totalsRowNumber}`,
      gridType: 'number',
      format: smallCurrencyFormat,
    }
    : {
      readOnly: true,
      value: '',
      key: `C${totalsRowNumber}`,
      expr: `=SUM(${getSumExprCells('C')})`,
      gridType: 'number',
      format: smallCurrencyFormat,
    };
  const totalValueCellFormat = {
    ...smallCurrencyFormat,
    maximumFractionDigitsWhenEditing: 4,
  };
  let TOTAL_VALUE_CELL: Partial<ICell>;
  if (isTargetValueReadOnly && sourceRowsSheetName) {
    TOTAL_VALUE_CELL = {
      alias: TARGET_VALUE_ALIAS,
      customKey: SOURCE_TOTALS_ROW_TARGET,
      readOnly: isDisabled || isTargetValueReadOnly,
      value: Number(targetValueFromBasket) || 0,
      gridType: 'number',
      key: `D${totalsRowNumber}`,
      expr: `=${sourceRowsSheetName}.${SOURCE_TOTALS_ROW_TARGET}`,
      format: totalValueCellFormat,
    };
  } else if (isTargetValueReadOnly) {
    TOTAL_VALUE_CELL = {
      alias: TARGET_VALUE_ALIAS,
      customKey: SOURCE_TOTALS_ROW_TARGET,
      readOnly: isDisabled || isTargetValueReadOnly,
      value: Number(targetValueFromBasket) || 0,
      gridType: 'number',
      key: `D${totalsRowNumber}`,
      expr: String(targetValueFromBasket || 0),
      format: totalValueCellFormat,
    };
  } else {
    TOTAL_VALUE_CELL = {
      alias: TARGET_VALUE_ALIAS,
      readOnly: isDisabled || isTargetValueReadOnly,
      value: '',
      gridType: 'number',
      key: `D${totalsRowNumber}`,
      expr: `=SUM(${getSumExprCells('D')})`,
      format: totalValueCellFormat,
    };
  }
  cells[TOTAL_LABEL.key] = TOTAL_LABEL;
  cells[SHARES_TOTAL_CELL.key] = SHARES_TOTAL_CELL as ICell;
  cells[PER_SHARE_TOTAL_CELL.key] = PER_SHARE_TOTAL_CELL as ICell;
  cells[`D${totalsRowNumber}`] = TOTAL_VALUE_CELL as ICell;

  rowConfig.forEach((row, rowIdx, rowConfigArray) => {
    if (!row.skipParse) {
      const rowNumber = rowIdx + 1;

      const security_name_cell = {
        alias: SECURITY_ALIAS,
        key: `A${rowNumber}`,
        columnLegend: 'A',
        expr: row.security.security,
        readOnly: isDisabled,
        ignoreRowCopy: true,
        isRequired: true,
        className: 'text-left subtitle-indented titles-column-selector',
        dataEditor: (props: any) => <SecurityGridSelect optionsTextAlign="left" {...props} />,
        valueViewer: (props: any) => <SecuritySelectionViewer {...props} />,
      };
      const shares_cell = {
        key: `B${rowNumber}`,
        gridType: 'number',
        readOnly: isDisabled,
        isRequired: true,
        alias: SHARES_ALIAS,
        value: row.security.shares,
        format: SHARES_TOTAL_CELL.format,
      };

      const per_share_value_cell = isTargetValueReadOnly
        ? {
          key: `C${rowNumber}`,
          value: Number(row.security.per_share || targetValueFromBasket) || 0,
          expr: `=D${totalsRowNumber}`,
          gridType: 'number',
          readOnly: isDisabled || isTargetValueReadOnly,
          format: PER_SHARE_TOTAL_CELL.format,
          alias: PER_SHARE_ALIAS,
        }
        : {
          key: `C${rowNumber}`,
          value: row.security.per_share,
          gridType: 'number',
          format: PER_SHARE_TOTAL_CELL.format,
          alias: PER_SHARE_ALIAS,
        };
      const rowShares = Number(row.security.shares) || 0;
      const rowTotalValue = rowShares * (Number(targetValueFromBasket) || 0);
      const total_value_cell = isTargetValueReadOnly
        ? {
          key: `D${rowNumber}`,
          expr: `=B${rowNumber}*D${totalsRowNumber}`,
          value: rowTotalValue,
          gridType: 'number',
          defaultValue: 0,
          format: TOTAL_VALUE_CELL.format,
          alias: TOTAL_VALUE_ALIAS,
        }
        : {
          key: `D${rowNumber}`,
          expr: `=B${rowNumber} * C${rowNumber}`,
          gridType: 'number',
          defaultValue: 0,
          format: TOTAL_VALUE_CELL.format,
          alias: TOTAL_VALUE_ALIAS,
        };
      cells[security_name_cell.key] = security_name_cell as ICell;
      cells[shares_cell.key] = shares_cell as ICell;
      cells[per_share_value_cell.key] = per_share_value_cell as ICell;
      cells[total_value_cell.key] = total_value_cell as ICell;
    }
  });
  return cells;
};
